import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { can, fieldVisible, fieldWritable } from '../auth/me';
import { categoryLabels, useCategories } from '../catalog/ItemForm';
import { useMe } from '../auth/useMe';
import { parsePln, plnToInput } from '../catalog/money';
import { ConflictNotice } from '../ui/ConflictNotice';
import { QuickPicker, type PickerOption } from '../ui/QuickPicker';
import { sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';
import { PRICES_RESOURCE, SUBRENTALS_KEY, type PriceView } from './types';
import { NotesInput } from '../ui/NotesInput';

type ItemOption = PickerOption & { id: string; categoryId: string | null };

async function searchItems(q: string, signal: AbortSignal): Promise<ItemOption[]> {
  const res = await api<{ items: { id: string; name: string; categoryId?: string | null }[] }>(`/api/catalog/items?q=${encodeURIComponent(q)}`, { signal });
  return res.items.slice(0, 20).map((i) => ({ key: i.id, id: i.id, name: i.name, details: [], categoryId: i.categoryId ?? null }));
}

/**
 * One position of a supplier's catalog: our item (picked from the catalog) or one the supplier has and we do not
 * (its name), with the price per day. On edit only what changed is sent (rule 7a); the price only to whom may see it.
 * `initialItem` (a new position, „Dodaj w katalogu dostawcy” — owner 2026-10-23 20:59): the form opens in the mode
 * „Pozycja naszego katalogu” with that item linked. `onSaved`: after a save, before `onClose`. `onSubmitted`: after
 * `onClose`, when „Zapisz” saved (not a save from „Masz niezapisane zmiany” on the way out).
 */
export function PriceForm({
  supplierId,
  price,
  initialItem,
  onSaved,
  onClose,
  onSubmitted,
}: {
  supplierId: string;
  price?: PriceView;
  initialItem?: { id: string; name: string; categoryId: string | null };
  onSaved?: () => void;
  onClose: () => void;
  onSubmitted?: (() => void) | undefined;
}) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  // owner 2026-10-22 14:53: a new position is by default only in the supplier's catalog
  const [own, setOwn] = useState(price ? price.itemId !== null : !!initialItem);
  const [item, setItem] = useState<{ id: string; name: string; categoryId: string | null } | null>(price?.itemId ? { id: price.itemId, name: price.itemName ?? '', categoryId: price.categoryId ?? null } : !price && initialItem ? initialItem : null);
  // owner 2026-10-23 20:22: a position only the supplier has takes one of OUR categories ("" = none)
  const ownCategory = price && !price.itemId ? (price.categoryId ?? null) : null;
  const [categoryId, setCategoryId] = useState(ownCategory ?? '');
  const [name, setName] = useState(price?.name ?? '');
  const [amount, setAmount] = useState(price?.priceDayGrosze !== undefined ? plnToInput(price.priceDayGrosze) : '');
  const [clientAmount, setClientAmount] = useState(price?.clientPriceDayGrosze != null ? plnToInput(price.clientPriceDayGrosze) : '');
  const [notes, setNotes] = useState(price?.notes ?? '');
  const [quantity, setQuantity] = useState(price?.quantity != null ? String(price.quantity) : '');
  const [description, setDescription] = useState(price?.description ?? '');
  const [invalid, setInvalid] = useState('');
  const seeCategory = !!me && fieldVisible(me, PRICES_RESOURCE, 'categoryId');
  const categories = useCategories(seeCategory && !!me && can(me, 'catalog.categories', 'view')).data?.categories ?? [];
  const labels = categoryLabels(categories);
  const save = useMutation({
    mutationFn: (fields: Record<string, unknown>) =>
      price ? api(`/api/subrentals/prices/${price.id}`, { method: 'PATCH', body: { version: price.version, ...fields } }) : api('/api/subrentals/prices', { method: 'POST', body: { supplierId, ...fields } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SUBRENTALS_KEY });
      onSaved?.();
      onClose();
      if (submitted.current) onSubmitted?.();
    },
  });
  /** The save in flight came from „Zapisz” (the form's submit). */
  const submitted = useRef(false);
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const now = { own, item: item?.id ?? null, categoryId, name, amount, clientAmount, notes, quantity, description };
  const opening = useOpening(now);
  const saveNow = useRef<() => Promise<boolean>>(async () => false);
  const guardRef = useUnsavedGuard({ dirty: !sameValues(now, opening), save: () => saveNow.current() });
  if (!me) return null;
  const seePrice = fieldVisible(me, PRICES_RESOURCE, 'priceDayGrosze');
  const seeClientPrice = fieldVisible(me, PRICES_RESOURCE, 'clientPriceDayGrosze');
  const seeQty = fieldVisible(me, PRICES_RESOURCE, 'quantity');
  const seeDescription = fieldVisible(me, PRICES_RESOURCE, 'description');
  /** What to send (only what changed), or null with the reason shown. */
  function fields(): Record<string, unknown> | null {
    const fail = (why: string) => (setInvalid(why), null);
    setInvalid('');
    if (own && !item) return fail('Wybierz pozycję z katalogu');
    if (!own && name.trim() === '') return fail('Wpisz nazwę sprzętu u dostawcy');
    let priceDayGrosze: number | undefined;
    if (seePrice) {
      try {
        priceDayGrosze = parsePln(amount);
      } catch (error) {
        return fail(error instanceof Error ? error.message : 'Nieprawidłowa cena');
      }
    }
    // owner 2026-10-22: "Cena dla klienta" is optional — empty = our usual price in the quote
    let clientPriceDayGrosze: number | null | undefined;
    if (seeClientPrice) {
      try {
        clientPriceDayGrosze = clientAmount.trim() === '' ? null : parsePln(clientAmount);
      } catch (error) {
        return fail(`Cena dla klienta: ${error instanceof Error ? error.message : 'nieprawidłowa kwota'}`);
      }
    }
    const qty = quantity.trim() === '' ? null : Number(quantity.trim());
    if (qty !== null && (!Number.isInteger(qty) || qty < 0)) return fail('Ilość posiadanych: liczba całkowita od 0 (albo puste pole)');
    const itemId = own ? item!.id : null;
    const fields: Record<string, unknown> = {
      ...(!price || itemId !== price.itemId ? { itemId } : {}),
      // the supplier's own name stays as „Nazwa u dostawcy” also after „To samo co u mnie” (owner 2026-10-23 21:46)
      ...((!own || name.trim() !== '') && (!price || name.trim() !== price.name) ? { name: name.trim() } : {}),
      ...(priceDayGrosze !== undefined && (!price || priceDayGrosze !== price.priceDayGrosze) ? { priceDayGrosze } : {}),
      ...(clientPriceDayGrosze !== undefined && ((!price && clientPriceDayGrosze !== null) || (price && clientPriceDayGrosze !== (price.clientPriceDayGrosze ?? null))) ? { clientPriceDayGrosze } : {}),
      ...((!price && notes.trim() !== '') || (price && notes.trim() !== (price.notes ?? '')) ? { notes: notes.trim() } : {}),
      // owner 2026-10-21: "ilość posiadanych" and "opis" next to the price and notes (only fields the role sees, rule 7a)
      ...(seeQty && ((!price && qty !== null) || (price && qty !== (price.quantity ?? null))) ? { quantity: qty } : {}),
      // the item's category counts for our item's position: none of its own is sent
      ...(!own && seeCategory && (categoryId || null) !== ownCategory ? { categoryId: categoryId || null } : {}),
      ...(seeDescription && ((!price && description.trim() !== '') || (price && description.trim() !== (price.description ?? ''))) ? { description: description.trim() } : {}),
    };
    return fields;
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const f = fields();
    if (!f) return;
    if (price && Object.keys(f).length === 0) {
      onClose();
      onSubmitted?.();
      return;
    }
    submitted.current = true;
    save.mutate(f, { onSettled: () => void (submitted.current = false) });
  }
  saveNow.current = async () => {
    const f = fields();
    if (!f) return false;
    if (!price || Object.keys(f).length > 0) await save.mutateAsync(f);
    return true;
  };
  const error = save.error instanceof ApiError ? save.error : null;
  return (
    <form ref={guardRef} className="group supplier-price-form" onSubmit={submit} noValidate aria-label={price ? 'Zmiana ceny dostawcy' : 'Nowa cena dostawcy'}>
      <div className="segment" role="radiogroup" aria-label="Rodzaj pozycji">
        <button type="button" role="radio" aria-checked={!own} aria-pressed={!own} onClick={() => setOwn(false)}>
          Tylko u dostawcy
        </button>
        <button type="button" role="radio" aria-checked={own} aria-pressed={own} onClick={() => setOwn(true)}>
          Pozycja naszego katalogu
        </button>
      </div>
      <div className="field-row">
        {own ? (
          item ? (
            <p className="rental-item">
              <strong>{item.name}</strong>
              {name.trim() !== '' && <span className="muted">Nazwa u dostawcy: {name.trim()}</span>}
              <button type="button" className="btn small ghost" onClick={() => setItem(null)}>
                Zmień
              </button>
            </p>
          ) : (
            <QuickPicker<ItemOption> label="Pozycja z katalogu" placeholder="Szukaj w katalogu…" search={searchItems} onPick={(o) => setItem({ id: o.id, name: o.name, categoryId: o.categoryId })} />
          )
        ) : (
          <>
            <label className="field">
              <span>Nazwa u dostawcy</span>
              <input type="text" maxLength={300} value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            {/* owner 2026-10-23 21:46: „to jest ta sama pozycja u nas w katalogu” — choosing it links the position (the
                „Pozycja naszego katalogu” mode, our item's name and category win, this name stays) */}
            {fieldWritable(me, PRICES_RESOURCE, 'itemId') && (
              <div className="field" data-feature-id="subrentals.catalog_match">
                <QuickPicker<ItemOption>
                  label="To samo co u mnie:"
                  placeholder="Szukaj w naszym katalogu…"
                  search={searchItems}
                  onPick={(o) => {
                    setItem({ id: o.id, name: o.name, categoryId: o.categoryId });
                    setOwn(true);
                  }}
                />
              </div>
            )}
          </>
        )}
        {seeCategory &&
          (own ? (
            item && (
              <p className="field muted" data-testid="item-category">
                <span>Kategoria</span>
                {(item.categoryId && labels.find((c) => c.id === item.categoryId)?.label) || 'Bez kategorii'} — z naszego katalogu
              </p>
            )
          ) : (
            labels.length > 0 && (
              <label className="field">
                <span>Kategoria</span>
                <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} title="Nasze kategorie: pozycja trafia do nich w katalogu dostawcy i w sekcje kosztorysu">
                  <option value="">Bez kategorii</option>
                  {labels.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
            )
          ))}
        {seePrice && (
          <label className="field narrow-field">
            <span>Cena za dobę (zł)</span>
            <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
        )}
        {seeClientPrice && (
          <label className="field narrow-field">
            <span>Cena dla klienta (zł)</span>
            <input inputMode="decimal" value={clientAmount} onChange={(e) => setClientAmount(e.target.value)} placeholder="jak w naszym" title="Za dobę; trafia do kosztorysu jako cena pozycji. Puste — nasza zwykła cena" />
          </label>
        )}
        {seeQty && (
          <label className="field narrow-field">
            <span>Ilość posiadanych</span>
            <input inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="nie wiadomo" />
          </label>
        )}
      </div>
      <div className="field-row">
        {seeDescription && (
          <label className="field">
            <span>Opis</span>
            <input type="text" maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="np. model, wersja, w zestawie" />
          </label>
        )}
        <label className="field">
          <span>Uwagi</span>
          <NotesInput maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>
      {invalid && (
        <p className="alert" role="alert">
          {invalid}
        </p>
      )}
      {error?.status === 409 && <ConflictNotice onReload={() => { void queryClient.invalidateQueries({ queryKey: SUBRENTALS_KEY }); onClose(); }} />}
      {error && error.status !== 409 && (
        <p className="alert" role="alert">
          {error.message}
        </p>
      )}
      <div className="actions">
        <button className="btn primary small" type="submit" disabled={save.isPending}>
          {price ? 'Zapisz' : 'Dodaj'}
        </button>
        <button className="btn small" type="button" onClick={onClose}>
          Anuluj
        </button>
      </div>
    </form>
  );
}
